/**
 * Bey definitions, stats, archetypes, and the type triangle.
 * Pure data + pure functions. No Phaser.
 */

export type BeyType = 'attack' | 'defense' | 'stamina' | 'balance';

/** Spin direction — flavor-only in v1 (DESIGN §10.6). */
export type SpinDir = 'right' | 'left';

export interface BeyStats {
  /** Spin damage + knockback dealt on a clash. */
  attack: number;
  /** Reduces spin loss + knockback taken. */
  defense: number;
  /** Slows idle spin drain. */
  stamina: number;
  /** Momentum: harder to knock around, hits harder. */
  weight: number;
  /** Responsiveness to steering input. */
  speed: number;
  /** Resists the burst meter. */
  durability: number;
}

export interface BeyDef {
  id: string;
  name: string;
  type: BeyType;
  spinDir: SpinDir;
  /** Display color (0xRRGGBB) used for placeholder art + UI accents. */
  color: number;
  /** Secondary accent color for art detailing. */
  accent: number;
  blurb: string;
  stats: BeyStats;
  /** Texture base id to load sprites from (defaults to `id`). Custom beys point
   *  this at their frame so they reuse existing art. */
  artId?: string;
}

/** Texture base id for a bey's sprites (`bey-<artBase>-face` / `-blur`). */
export const artBase = (def: BeyDef): string => def.artId ?? def.id;

/** The starting roster (DESIGN §4.1), one bey per archetype. Stats 1–10. */
export const ROSTER: readonly BeyDef[] = [
  {
    id: 'phoenix',
    name: 'Blaze Phoenix',
    type: 'attack',
    spinDir: 'right',
    color: 0xff5a2a,
    accent: 0xffc24a,
    blurb: 'Glass cannon. Hits hard and fast — the only realistic burster — but bleeds spin if reckless.',
    stats: { attack: 9, defense: 3, stamina: 3, weight: 5, speed: 8, durability: 4 },
  },
  {
    id: 'turtle',
    name: 'Aegis Shellguard',
    type: 'defense',
    spinDir: 'right',
    color: 0x4f86d6,
    accent: 0xc2cede,
    blurb: 'Heavy tank. Shrugs off hits and counters; struggles to close out stamina types.',
    stats: { attack: 3, defense: 9, stamina: 5, weight: 9, speed: 3, durability: 8 },
  },
  {
    id: 'dragon',
    name: 'Verdant Wyrm',
    type: 'stamina',
    spinDir: 'left',
    color: 0x33b070,
    accent: 0xe6c14e,
    blurb: 'Marathon top. Plays center and outlasts everyone — unless an attacker catches it.',
    stats: { attack: 3, defense: 5, stamina: 9, weight: 3, speed: 5, durability: 5 },
  },
  {
    id: 'owl',
    name: 'Twilight Owl',
    type: 'balance',
    spinDir: 'right',
    color: 0xa07fd6,
    accent: 0xdcd6ec,
    blurb: 'All-rounder. No type advantage, but no weakness either — steady in every matchup.',
    stats: { attack: 5, defense: 6, stamina: 6, weight: 5, speed: 6, durability: 6 },
  },
] as const;

/** The player's custom-built bey, if any (a singleton; see parts.ts). */
let customBey: BeyDef | null = null;
export function setCustomBey(def: BeyDef | null): void {
  customBey = def;
}

export function getBeyDef(id: string): BeyDef {
  const def = ROSTER.find((b) => b.id === id);
  if (def) return def;
  if (customBey && customBey.id === id) return customBey;
  throw new Error(`Unknown bey id: ${id}`);
}

/**
 * Type triangle: attack > stamina > defense > attack. Balance is neutral.
 * Returns a damage multiplier for `attacker` hitting `defender`.
 */
export function typeAdvantage(
  attacker: BeyType,
  defender: BeyType,
  advantage: number,
  disadvantage: number,
): number {
  const beats: Record<BeyType, BeyType | null> = {
    attack: 'stamina',
    stamina: 'defense',
    defense: 'attack',
    balance: null,
  };
  if (attacker === 'balance' || defender === 'balance') return 1;
  if (beats[attacker] === defender) return advantage;
  if (beats[defender] === attacker) return disadvantage;
  return 1; // same type
}

/**
 * Pick the AI opponent for a player's chosen bey: prefer the bey that the
 * player's pick is *weak* to (counter-pick), else a random other bey.
 */
export function pickOpponent(playerId: string, rng: () => number = Math.random): BeyDef {
  const player = getBeyDef(playerId);
  const counters = ROSTER.filter(
    (b) => b.id !== playerId && typeAdvantage(b.type, player.type, 2, 0.5) > 1,
  );
  const pool = counters.length > 0 ? counters : ROSTER.filter((b) => b.id !== playerId);
  return pool[Math.floor(rng() * pool.length) % pool.length];
}
